import { Permission, UserRole } from "react-ai-platform-auth";

import { Options } from "./components/Forms/types";

export enum InputTypes {
  TEXT = "text",
  IMAGE = "image",
  VIDEO = "video",
  AUDIO = "audio",
}

export enum DateFilterTypes {
  ANYTIME = "anytime",
  PAST_WEEK = "past_week",
  PAST_MONTH = "past_month",
  PAST_YEAR = "past_year",
  DATE = "date",
  DATE_RANGE = "date_range",
}

export enum OutputTypes {
  FREE_TEXT = "free_text",
  CATEGORIES = "categories",
  IMAGE_SELECTION = "image_selection",
  TEXT_ANNOTATION = "text_annotation",
  IMAGE_ANNOTATION = "image_annotation",
  AUDIO_COLLECTION = "audio",
  IMAGE_COLLECTION = "image",
  VIDEO_COLLECTION = "video",
  SEMANTIC_ANNOTATION = "semantic_annotation",
  DATE = "date",
  TIME = "time",
  NUMBER = "number",
}

export enum CommentOption {
  REQUIRED = "required",
  NONE = "none",
  OPTIONAL = "optional",
}

export enum DistributionType {
  PER_ITEM = "per_item",
  PER_GROUP = "per_group",
}

export enum ImageAnnotationTypes {
  CIRCLE = "circle",
  BOUNDING_BOX = "bounding_box",
  KEYPOINT = "keypoint",
  POLYGON = "polygon",
  LINE = "line",
}

export enum ProjectStatus {
  CREATED = "created",
  ARCHIVED = "archived",
}

export enum ExportFormat {
  CSV = "csv",
  RAW_JSON = "raw_json",
  DELIVERY_JSON = "delivery_json",
  DELIVERY_CSV = "delivery_csv",
}

export enum MembershipType {
  ORGANIZATION = "organization",
}

export enum ItemType {
  NORMAL = "normal",
  GOLD_STANDARD = "gold_standard",
}

export enum AssessmentCategory {
  DOMAIN = "domain",
  PRACTICE = "practice",
  PROJECT = "project",
}

export enum ProjectType {
  CUSTOM = "custom",
  IMAGE_ANNOTATION = "image_annotation",
  AUDIO_TRANSCRIPTION = "audio_transcription",
  VIDEO_TRANSCRIPTION = "video_transcription",
  SEMANTIC_SEGMENTATION = "semantic_segmentation",
  SENTIMENT_ANALYSIS = "sentiment_analysis",
  SEARCH_RELEVANCE = "search_relevance",
  AUDIO_RECORDING = "audio_recording",
  IMAGE_COLLECTION = "image_collection",
  NAMED_ENTITY_RECOGNITION = "named_entity_recognition",
  PARTS_OF_SPEECH_TAGGING = "parts_of_speech_tagging",
  PHONETIC_TRANSCRIPTION = "phonetic_transcription",
  INTENT_VARIATION = "intent_variation",
  IMAGE_CATEGORIZATION = "image_categorization",
  TEXT_CATEGORIZATION = "text_categorization",
  AUDIO_CATEGORIZATION = "audio_categorization",
  VIDEO_CATEGORIZATION = "video_categorization",
  LANGUAGE_TEST = "language_test",
  READING_COMPREHENSION_TEST = "reading_comprehension_test",
  LISTENING_COMPREHENSION_TEST = "listening_comprehension_test",
  GRAMMAR_TEST = "grammar_test",
  CONTENT_CREATION_TEST = "content_creation_test",
  SURVEY = "survey",
  AGREEMENT = "agreement",
  CAPTION_TO_IMAGE_MATCHING = "caption_to_image_matching",
  VIDEO_COLLECTION = "video_collection",
  MOBILE_SURVEY = "mobile_survey",
}

export enum FieldSize {
  LARGE = "large",
  MEDIUM = "medium",
  SMALL = "small",
}

export enum InputMethod {
  CAMERA_OR_GALLERY = "camera_or_gallery",
  CAMERA = "camera",
  GALLERY = "gallery",
}

export enum FieldValidTypes {
  NONE = "none",
  MAX_COUNT = "max_count",
}

export type HelperText = string | null;

export type InputConfig = Readonly<{
  type: InputTypes;
  helperText?: HelperText;
}>;

export type AssessmentInputConfig = Readonly<{
  type?: InputTypes;
  helperText?: HelperText;
}>;

export type ItemCategory = Readonly<{
  key: string;
  name: string;
  itemLimit: number;
}>;

export type FollowupCondition = Readonly<{
  targetOutputIndex: number;
  choiceIndexes?: ReadonlyArray<number>;
  choiceIds?: ReadonlyArray<string>;
}>;

export type InputTag = {
  id?: string;
  name: string;
  tag: string;
};

export type InputTagCategory = {
  id?: string;
  name: string;
  tags: ReadonlyArray<InputTag>;
};

export type InputClassCategory = {
  name: string;
  labels: ReadonlyArray<AnnotationLabel>;
};

export type WithFollowupCondition<T> = T &
  Readonly<{
    condition?: FollowupCondition;
  }>;

export type FreeText = Readonly<{
  type: OutputTypes.FREE_TEXT;
  helperText?: HelperText;
  inputTagCategories?: ReadonlyArray<InputTagCategory> | null;
  fieldSize?: FieldSize;
  fieldTextType?: FieldValidTypes;
  isRequired: boolean;
}>;

export type Choice = Readonly<{
  name: string;
  id?: string;
}>;

export type Categories = Readonly<{
  type: OutputTypes.CATEGORIES;
  helperText?: HelperText;
  maxChoices: number | null;
  choices: ReadonlyArray<Choice>;
  isRequired: boolean;
}>;

export type AnnotationLabel = Readonly<{
  customId?: string;
  text: string;
}>;

export type ImageSelection = Readonly<{
  type: OutputTypes.IMAGE_SELECTION;
  helperText?: HelperText;
  maxChoices: number | null;
  isRequired: boolean;
}>;

export type TextAnnotation = Readonly<{
  type: OutputTypes.TEXT_ANNOTATION;
  allowOverlap: boolean;
  commentOption: CommentOption;
  labels: ReadonlyArray<AnnotationLabel>;
  inputClassCategories?: ReadonlyArray<InputClassCategory> | null;
}>;

export type ImageAnnotationLabel = AnnotationLabel &
  Readonly<{
    annotationType: ImageAnnotationTypes;
  }>;

export type AnnotationLabelTypes = ImageAnnotationLabel | AnnotationLabel;

export type AudioCollection = Readonly<{
  type: OutputTypes.AUDIO_COLLECTION;
  audioHeuristicsEnabled: boolean;
}>;

export type ImageCollection = Readonly<{
  type: OutputTypes.IMAGE_COLLECTION;
  inputMethod: InputMethod;
}>;

export type VideoCollection = Readonly<{
  type: OutputTypes.VIDEO_COLLECTION;
  inputMethod: InputMethod;
}>;

export type ImageAnnotation = Readonly<{
  type: OutputTypes.IMAGE_ANNOTATION;
  commentOption: CommentOption;
  labels: ReadonlyArray<ImageAnnotationLabel>;
}>;

export type SemanticAnnotation = Readonly<{
  type: OutputTypes.SEMANTIC_ANNOTATION;
  labels: ReadonlyArray<AnnotationLabel>;
}>;

export type DateOutput = Readonly<{
  type: OutputTypes.DATE;
  helperText?: HelperText;
  isRequired: boolean;
}>;

export type TimeOutput = Readonly<{
  type: OutputTypes.TIME;
  helperText?: HelperText;
  isRequired: boolean;
}>;

export type NumberOutput = Readonly<{
  type: OutputTypes.NUMBER;
  helperText?: HelperText;
  isRequired: boolean;
}>;

export type OutputConfig =
  | WithFollowupCondition<FreeText>
  | WithFollowupCondition<Categories>
  | WithFollowupCondition<ImageSelection>
  | TextAnnotation
  | ImageAnnotation
  | AudioCollection
  | ImageCollection
  | VideoCollection
  | SemanticAnnotation
  | WithFollowupCondition<DateOutput>
  | WithFollowupCondition<TimeOutput>
  | WithFollowupCondition<NumberOutput>;

export type WithUuid<T> = T &
  Readonly<{
    uuid: string;
  }>;

export type HydratedFollowupCondition = Readonly<{
  targetOutputUuid: string;
  choiceIndexes: ReadonlyArray<number>;
}>;

export type WithHydratedCondition<T> = T &
  Readonly<{
    condition?: HydratedFollowupCondition;
  }>;

export type HydratedOutputConfig = WithUuid<
  | WithHydratedCondition<FreeText>
  | WithHydratedCondition<Categories>
  | WithHydratedCondition<ImageSelection>
  | TextAnnotation
  | ImageAnnotation
  | AudioCollection
  | ImageCollection
  | VideoCollection
  | SemanticAnnotation
  | WithHydratedCondition<DateOutput>
  | WithHydratedCondition<TimeOutput>
  | WithHydratedCondition<NumberOutput>
>;

type ContributorItemLimit = Readonly<{
  fixed?: number | null;
  percentage?: number | null;
}>;

type PreLabel = Readonly<{
  isAutomated: boolean;
}>;

export type UnsavedProject = Readonly<{
  name: string;
  description: string;
  inputConfigs: ReadonlyArray<InputConfig>;
  outputConfigs: ReadonlyArray<OutputConfig>;
  passCount: number;
  contributorItemLimit: ContributorItemLimit;
  distributionType: DistributionType;
  languages: ReadonlyArray<string>;
  preLabel: PreLabel;
  showSpeedControl?: boolean | null;
}>;

export type HydratedProject = Readonly<{
  name: string;
  description: string;
  inputConfigs: ReadonlyArray<InputConfig>;
  outputConfigs: ReadonlyArray<HydratedOutputConfig>;
  passCount: number;
  contributorItemLimit: ContributorItemLimit;
  distributionType: DistributionType;
  languages: ReadonlyArray<string>;
  preLabel: PreLabel;
  showSpeedControl?: boolean | null;
}>;

export type ProjectId = string;
export type ItemId = string;
export type AssessmentId = string;
export type GroupAssessmentId = string;

export type ProjectStats = Readonly<{
  available: number;
  completed: number;
  deleted: number;
  progress: number;
  total: number;
  unique: number;
}>;

export type Reward = Readonly<{
  perUnit: string;
}>;

// TODO: add other assignee types when available
export type ProjectAssigneeType = "api_client";

export type ProjectAssignee = Readonly<{
  id: string;
  type: ProjectAssigneeType;
}>;

export type RevisionSettings = Readonly<{
  isReturnToContributorEnabled: boolean;
  isReturnToPoolEnabled: boolean;
}>;

export type ProjectCallbackSettings = Readonly<{
  isCallbackOnItemCompleteEnabled: boolean;
}>;

export type ProjectDetails = Readonly<{
  id: ProjectId;
  name: string;
  description: string;
  instructions: string;
  referenceId: string;
  inputConfigs: ReadonlyArray<InputConfig>;
  outputConfigs: ReadonlyArray<OutputConfig>;
  passCount: number;
  timeLimit: number;
  estimatedTime: number | null;
  reward: Reward;
  preferredContributorIds: ReadonlyArray<UserId>;
  assignees: ReadonlyArray<ProjectAssignee>;
  scopes?: ReadonlyArray<string>;
  mtime: Readonly<Date>;
  contributorItemLimit: ContributorItemLimit;
  distributionType: DistributionType;
  isPublished: boolean;
  revisionSettings: RevisionSettings;
  callbackSettings: ProjectCallbackSettings;
  languages: ReadonlyArray<string>;
  preLabel: PreLabel;
  projectType: ProjectType;
  showSpeedControl?: boolean | null;
}>;

export type ProjectDownloadURLs = Readonly<{
  csvTemplateURL: string;
  jsonTemplateURL: string;
  labelDetailsURL: string;
  contributorURL: string;
  goldStandardReportURL: string;
  goldStandardItemReportURL: string;
  timeSpentPerItemURL: string;
  exportURL: (format: ExportFormat) => string;
}>;

export type AssessmentDownloadURLs = Readonly<{
  contributorURL: string;
  contributorItems: string;
  contributorItemOutputs: string;
  jsonTemplateURL: string;
  csvTemplateURL: string;
  exportURL: (format: ExportFormat) => string;
}>;

export type PresignedURL = string;

export type ProjectPartialUpdate = Readonly<{
  name?: string;
  description?: string;
  instructions?: string;
  passCount?: string;
  referenceId?: string;
  reward?: Reward;
  preferredContributorIds?: ReadonlyArray<UserId>;
  assignees?: ReadonlyArray<ProjectAssignee>;
  timeLimit?: number;
  estimatedTime?: number | null;
  isPublished?: boolean;
  revisionSettings?: Partial<RevisionSettings>;
  callbackSettings?: Partial<ProjectCallbackSettings>;
}>;

export type ProjectSummary = Readonly<{
  id: ProjectId;
  name: string;
  status: ProjectStatus;
  ctime: Readonly<Date>;
  description: string;
  inputConfigTypes: ReadonlyArray<InputTypes>;
  outputConfigTypes: ReadonlyArray<OutputTypes>;
  creatorEmail?: string; // deprecated - fetch from authal using creatorId when needed
  creatorId: string;
  projectType?: ProjectType;
}>;

export type ProjectSummaryList = ReadonlyArray<ProjectSummary>;

export type ProjectListPaginatedResult = PaginatedListResult<ProjectSummary>;

export type ProjectContributorSummary = Readonly<{
  contributorId: UserId;
  done: number;
  itemLimit: number | null;
}>;

export type ProjectContributorSummaryList = ReadonlyArray<ProjectContributorSummary>;

export type RevisionBatchId = string;

export type RevisionBatchAutomatedConfig = Readonly<{
  itemPercentage: number;
}>;

export type RevisionBatchSummary = Readonly<{
  id: RevisionBatchId;
  projectId: ProjectId;
  name: string;
  creatorId: string;
  creatorEmail?: string; // deprecated - fetch from authal using creatorId when needed
  ctime: Readonly<Date>;
  mtime: Readonly<Date>;
  description?: string;
  isAutomated: boolean;
  projectType?: ProjectType;
}>;

export type RevisionBatchSummaryList = ReadonlyArray<RevisionBatchSummary>;

export type UnsavedRevisionBatch = Readonly<{
  projectId: ProjectId;
  name: string;
  instructions: string;
  isAutomated?: boolean;
  automatedConfig?: RevisionBatchAutomatedConfig;
  preferredContributorIds?: ReadonlyArray<UserId>;
}>;

export type RevisionBatchDetails = Readonly<{
  id: RevisionBatchId;
  projectId: ProjectId;
  name: string;
  instructions: string;
  preferredContributorIds: ReadonlyArray<UserId>;
  scopes?: ReadonlyArray<string>;
  creatorId: string;
  creatorEmail?: string; // deprecated - fetch from authal using creatorId when needed
  ctime: Readonly<Date>;
  mtime: Readonly<Date>;
  description?: string;
  automatedConfig: RevisionBatchAutomatedConfig;
}>;

export type RevisionBatchPartialUpdate = Readonly<{
  name?: string;
  instructions?: string;
  preferredContributorIds?: ReadonlyArray<UserId>;
  description?: string;
  automatedConfig?: RevisionBatchAutomatedConfig;
}>;

export type RevisionBatchStats = Readonly<{
  done: number;
  pending: number;
  total: number;
  unavailable: number;
}>;

export type ItemIds = ReadonlyArray<string>;

export type AddBulkRevisions = Readonly<{
  id: RevisionBatchId;
  itemIds: ItemIds;
}>;

export type UserId = string;

export type Agreement = Readonly<{
  type: string;
  version: string;
  acceptedTime?: Readonly<Date>;
}>;

export type UnsavedAgreement = Readonly<{
  type: string;
  version: string;
  isAccepted: boolean;
}>;

export type UserProfile = Readonly<{
  agreements: ReadonlyArray<Agreement>;
}>;

export type UserProfilePartialUpdate = Readonly<{
  agreements?: ReadonlyArray<UnsavedAgreement>;
}>;

export type UserDetails = Readonly<{
  id: UserId;
  isEditable: boolean;
  email: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  userInitial: string;
  memberships: ReadonlyArray<UserMembership>;
  ctime: Readonly<Date>;
  mtime: Readonly<Date>;
  lastAuthTime?: Readonly<Date>;
  profile: UserProfile;
}>;

export type UserDetailsPartialUpdate = Readonly<
  Partial<{
    firstName: string;
    middleName?: string;
    lastName: string;
    profile: UserProfilePartialUpdate;
  }>
>;

export type UserMembership = Readonly<{
  id: string;
  role: UserRole;
  type: MembershipType;
  notValidAfter?: Readonly<Date>;
  organization?: OrganizationSummary;
}>;

export type UserMembershipWithPermissions = UserMembership &
  Readonly<{ permissions: ReadonlyArray<Permission> }>;

export type AdminUserPartialUpdate = Readonly<
  Partial<
    UserDetailsPartialUpdate & {
      memberships: ReadonlyArray<UserMembership>;
    }
  >
>;

export type UserSummary = Readonly<{
  id: UserId;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  lastAuthTime?: Readonly<Date>;
  email: string;
  memberships: ReadonlyArray<UserMembership>;
}>;

export type UserSummaryList = ReadonlyArray<UserSummary>;

export type PaginatedListResult<T> = Readonly<{
  results: ReadonlyArray<T>;
  hasNextPage?: boolean;
  pageCount?: number;
}>;

export type UserSummaryListPaginatedResult = PaginatedListResult<UserSummary>;

export type EmailAddress = string;

export type UserInvitee = Readonly<{
  email: EmailAddress;
}>;

export type InviteUsersResult = Readonly<{
  invited: ReadonlyArray<UserInvitee>;
  duplicates: ReadonlyArray<UserInvitee>;
  error: string | null;
}>;

export type OrganizationId = string;

export type OrganizationCreator = Readonly<{
  id: UserId;
  email?: string;
}>;

export type Organization = Readonly<{
  id: OrganizationId;
  name: string;
  creator: OrganizationCreator;
  ctime: Readonly<Date>;
  mtime: Readonly<Date>;
  notValidAfter?: Readonly<Date>;
}>;

export type OrganizationSummary = Readonly<{
  id: OrganizationId;
  name: string;
}>;

export type OrganizationList = ReadonlyArray<Organization>;

export type UnsavedOrganization = Readonly<{ name: string } & UnsavedOrganizationUser>;

export type UnsavedOrganizationUser = Readonly<{
  email: string;
  firstName: string;
  lastName: string;
  profile?: UserProfile;
}>;

export type OrganizationPartialUpdate = Readonly<{
  name?: string;
  notValidAfter?: Readonly<Date> | null;
}>;

export type ApiClientId = string;

export type ApiClientSummary = Readonly<{
  id: string;
  name: string;
}>;
export type ApiClientSummaryList = ReadonlyArray<ApiClientSummary>;

export type ContributorJob = ProjectSummary | RevisionBatchSummary;
export type ContributorJobList = ReadonlyArray<ContributorJob>;

export type TimeSpent = Readonly<{
  contributorId: string;
  contributorName: string | null;
  low: number;
  median: number;
  high: number;
  itemCount: number;
}>;

export type TimeSpentList = Array<TimeSpent>;

export type ProjectProgress = Readonly<{
  projectId: ProjectId;
  available: number;
  complete: number;
  pending: number;
  revisionsAdded: number;
  revisionsReviewed: number;
  revisionsApproved: number;
  revisionsAmended: number;
  date: Date;
}>;

export type ProjectProgressList = Array<ProjectProgress>;

export type AnswerDistributionContributor = Readonly<{
  totalItems: number;
  contributorId: string;
  contributorName: string;
}>;

export type AnswerDistributionChoiceMap = Readonly<{
  choiceId: string;
  choiceName: string;
  contributors: Array<AnswerDistributionContributor>;
}>;

export type AnswerDistributionChoices = Readonly<{
  choiceId: string;
  choiceName: string;
  isPopular: boolean;
}>;

export type AnswerDistributionList = Readonly<{
  choices: Array<AnswerDistributionChoices>;
  answers: Array<AnswerDistributionChoiceMap>;
}>;

export type AssessmentSummary = Readonly<{
  id: AssessmentId;
  name: string;
  assessmentCategory: AssessmentCategory;
  description: string;
  ctime: Readonly<Date>;
  creatorId: UserId;
  assessmentType: ProjectType;
}>;

export type GroupAssessment = Readonly<{
  id: GroupAssessmentId;
  name: string;
  description: string;
  creatorId: UserId;
  ctime: Readonly<Date>;
  mtime: Readonly<Date>;
  assessments: AssessmentSummaryList;
  assignedContributorIds: ReadonlyArray<UserId>;
  scopes?: ReadonlyArray<string>;
}>;

export type AssessmentSummaryList = ReadonlyArray<AssessmentSummary>;
export type AssessmentSummaryListPaginatedResult = PaginatedListResult<AssessmentSummary>;
export type GroupAssessmentList = ReadonlyArray<GroupAssessment>;
export type GroupAssessmentListPaginatedResult = PaginatedListResult<GroupAssessment>;

export type HydratedAssessment = Readonly<{
  name: string;
  assessmentCategory: AssessmentCategory;
  assessmentType: ProjectType;
  isAutomatedScoring: boolean;
  description: string;
  inputConfigs: ReadonlyArray<AssessmentInputConfig>;
  outputConfigs: ReadonlyArray<HydratedOutputConfig>;
  languages: ReadonlyArray<string>;
  sessionLimit?: number | null;
  scoreThreshold?: number | null;
  assessmentCategoryType?: AssessmentCategoryTypes;
  itemCategories?: ReadonlyArray<ItemCategory>;
  isDynamicOutputConfig?: boolean;
}>;

export type UnsavedAssessment = Readonly<{
  name: string;
  assessmentCategory: AssessmentCategory;
  // TODO: create own AssessmentType
  assessmentType: ProjectType;
  isAutomatedScoring: boolean;
  description: string;
  inputConfigs: ReadonlyArray<AssessmentInputConfig>;
  outputConfigs: ReadonlyArray<OutputConfig>;
  languages: ReadonlyArray<string>;
  sessionLimit?: number | null;
  scoreThreshold?: number | null;
  itemCategories?: ReadonlyArray<ItemCategory>;
}>;

export type UnsavedGroupAssessment = Readonly<{
  name: string;
  description: string;
  assessmentIds: ReadonlyArray<AssessmentId>;
}>;

export type AssessmentContributorJobPostsSummary = Readonly<{
  contributorId: UserId;
  jobPostId: string;
  jobPostTitle: string;
}>;

export type AssessmentDetails = Readonly<{
  id: AssessmentId;
  name: string;
  assessmentCategory: AssessmentCategory;
  assessmentType: ProjectType;
  isAutomatedScoring: boolean;
  description: string;
  instructions: string;
  inputConfigs: ReadonlyArray<InputConfig>;
  outputConfigs: ReadonlyArray<OutputConfig>;
  reward: Reward;
  timeLimit: number;
  estimatedTime: number | null;
  preferredContributorIds: ReadonlyArray<UserId>;
  isPublished: boolean;
  scopes: ReadonlyArray<string>;
  languages: ReadonlyArray<string>;
  mtime: Readonly<Date>;
  sessionLimit?: number | null;
  scoreThreshold?: number | null;
  itemCategories?: ReadonlyArray<ItemCategory>;
  eligibilityCriteria?: EligibilityCriteria;
  isDynamicOutputConfig?: boolean;
  showFeedback?: boolean;
  showScore?: boolean;
  allowViewPrevious?: boolean;
  hideItemNavigation?: boolean;
  showCustomEndMessage?: boolean;
  customEndMessage?: string;
  contributorIds?: ReadonlyArray<UserId>;
  contributorJobPosts: ReadonlyArray<AssessmentContributorJobPostsSummary>;
}>;

export type AssessmentPartialUpdate = Readonly<{
  name?: string;
  description?: string;
  instructions?: string;
  reward?: Reward;
  timeLimit?: number;
  estimatedTime?: number | null;
  preferredContributorIds?: ReadonlyArray<UserId>;
  isPublished?: boolean;
  isArchived?: boolean;
  eligibilityCriteria?: EligibilityCriteria;
  allowViewPrevious?: boolean;
  hideItemNavigation?: boolean;
  showCustomEndMessage?: boolean;
  customEndMessage?: string;
}>;

export type AssessmentContributorSummary = Readonly<{
  contributorId: string;
  contributorScore: string;
  testItems: number;
  totalTimeSpent: string;
}>;

export type ServiceWithStatus<T> = Readonly<{
  status: "init" | "loading" | "loaded" | "error";
  error?: string;
  payload?: T;
}>;

export type ProjectTemplate = Readonly<{
  description: string;
  inputConfigs: ReadonlyArray<AssessmentInputConfig>;
  outputConfigs: ReadonlyArray<WithUuid<OutputConfig>>;
}>;

export type ProjectTypeFlags = Readonly<{
  canAdd: boolean;
  canChangeType: ReadonlyArray<boolean>;
  canRemove: ReadonlyArray<boolean>;
}>;

export type ProjectProgressWorker = Readonly<{
  id: string;
  name: string;
}>;

export type ProjectProgressWorkerList = ReadonlyArray<ProjectProgressWorker>;

export type ListItem<T> = T & { isChecked: boolean; isVisible: boolean };

export type ProjectUploadedItems = Readonly<{
  normal: number;
  goldStandard: number;
  preLabelled: number;
}>;

export type AssessmentUploadedItems = Readonly<{
  nrOfTestQuestions: number;
}>;

export type ProjectAvailableItem = { projectId: ProjectId; itemsAvailable: number };

export type ProjectAvailableItemList = ReadonlyArray<ProjectAvailableItem>;

export type RevisionBatchAvailableItem = { batchId: RevisionBatchId; itemsAvailable: number };

export type RevisionBatchAvailableItemList = ReadonlyArray<RevisionBatchAvailableItem>;

export type JobAvailableItem = ProjectAvailableItem | RevisionBatchAvailableItem;

export type JobAvailableItemList = ReadonlyArray<JobAvailableItem>;

export type AssessmentTestQuestion = {
  itemId: ItemId;
  key: string;
  attempts: number;
  medianTime: string;
  averageScore: string;
};

export type AssessmentTestQuestionScores = Readonly<{
  itemId: ItemId;
  averageScore: string;
}>;

export type AssessmentTestQuestionListPaginatedResult = PaginatedListResult<
  AssessmentTestQuestion
> & { totalItems?: number };

export type AnnotatorAgreementItem = Readonly<{
  totalAnswers: number;
  itemId: string;
  itemKey: string;
}>;

export type AnnotatorAgreementChoiceMap = Readonly<{
  choiceId: string;
  choiceName: string;
  items: Array<AnnotatorAgreementItem>;
}>;

export type AnnotatorAgreementList = Readonly<{
  choices: Array<AnswerDistributionChoices>;
  answers: Array<AnnotatorAgreementChoiceMap>;
}>;

export enum EligibilityPreferredType {
  MANUALLY = "manually",
  UNIVERSALLY = "universally",
  BY_CONTRIBUTOR_CHARACTERISTIC = "by_contributor_characteristic",
}

export enum EligibilityConditions {
  PRIMARY_LANGUAGE = "primary_language",
  SECONDARY_LANGUAGE = "secondary_language",
  LOCATION = "location",
}

export type EligibilityConditionType = {
  name: EligibilityConditions;
  values: Options;
};

export type EligibilityConditionMap = {
  name: EligibilityConditions;
  values: ReadonlyArray<string>;
};

export type EligibilityCriteria = {
  preferredType: EligibilityPreferredType;
  isAutomated: boolean;
  conditions: ReadonlyArray<EligibilityConditionMap>;
  blacklistedIds: ReadonlyArray<UserId>;
};

export enum AssessmentCategoryTypes {
  SINGLE_CATEGORY = "single_category",
  MULTIPLE_CATEGORY = "multiple_categories",
}

export type CmpUserSummary = Readonly<{
  authalId: string;
  firstName: string;
  lastName: string;
  projectUserRole: string;
  ofacStatus: string;
  email: string;
  primaryLanguage?: string;
  location?: string;
}>;

export type CmpUserSummaryListPaginatedResult = PaginatedListResult<CmpUserSummary>;

export enum ContextTypes {
  ASSESSMENTS = "assessments",
  PROJECTS = "projects",
}

export enum ReportTypes {
  COMPLETE_DATA = "complete_data",
  TESTING_REPORT = "testing_report",
  TESTING_REPORT_ITEM_LEVEL = "testing_report_item_level",
}

export enum ReportFormat {
  CSV = "text/csv",
}

export type ReportSummary = Readonly<{
  id: string;
  contextId: AssessmentId | ProjectId;
  contextType: ContextTypes;
  reportType: string;
  format: string;
  requester: Readonly<{
    userId: string;
    orgId: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }>;
  status: string;
  downloadUrl: string;
  ctime: Readonly<Date>;
  expireAt?: Readonly<Date>;
}>;

export enum AssignAssessmentAction {
  ADD = "Add",
  EDIT = "Edit",
}

export type ResponseResult<T> = Readonly<{
  results: ReadonlyArray<T>;
}>;

export type JobEntryUser = Readonly<{
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}>;

export type JobApplicationJobPost = Readonly<{
  id: number;
  title: string;
}>;

export type JobApplication = Readonly<{
  id: number;
  jobPost: JobApplicationJobPost;
  contributor: JobEntryUser;
}>;

export enum UserStatus {
  DEACTIVATED = "DEACTIVATED",
  ACTIVE = "ACTIVE",
}

export type OutputTypeOption = Readonly<{
  value: OutputTypes;
  label: string;
}>;

export type ProjectTypeOption = Readonly<{
  value?: ProjectType;
  label: string;
  options?: Options;
}>;
